import React from 'react'
import { 
  Box, 
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  SvgIcon
} from '@mui/material'
import { 
  Inventory as InventoryIcon,
  Receipt as SalesIcon,
  People as ClientsIcon,
  AccountBalance as XeroIcon,
  Store as OutletIcon,
  ShoppingCart as ProductIcon,
  Assessment as ReportsIcon,
  SearchOff as NoResultsIcon,
  CloudOff as NoDataIcon,
  FilterList as FilterIcon,
  Add as AddIcon
} from '@mui/icons-material'
import { poolwerxColors } from '../../../theme/colors'

// ============================================================================
// Types
// ============================================================================

export type EmptyStateType = 
  | 'no-data'
  | 'no-results' 
  | 'no-products'
  | 'no-sales'
  | 'no-clients'
  | 'no-reports'
  | 'filtered'
  | 'custom'

export interface EmptyStateProps {
  /** Type of empty state for predefined configurations */
  type?: EmptyStateType
  /** Custom title */
  title?: string
  /** Custom description */
  description?: string
  /** Custom icon */
  icon?: React.ReactNode
  /** Primary action button */
  primaryAction?: {
    label: string
    onClick: () => void
    variant?: 'contained' | 'outlined' | 'text'
  }
  /** Secondary action button */
  secondaryAction?: {
    label: string
    onClick: () => void
    variant?: 'contained' | 'outlined' | 'text'
  }
  /** Show as compact version */
  compact?: boolean
  /** Additional styling */
  sx?: object
}

// ============================================================================
// Empty State Configurations
// ============================================================================

const EMPTY_STATE_CONFIGS: Record<EmptyStateType, {
  icon: React.ReactNode
  title: string
  description: string
  iconColor?: string
}> = {
  'no-data': {
    icon: <NoDataIcon />,
    title: 'No Data Available',
    description: 'There is currently no data to display. Data may still be syncing from external systems.',
    iconColor: poolwerxColors.grey[600]
  },
  'no-results': {
    icon: <NoResultsIcon />,
    title: 'No Results Found',
    description: 'Your search or filters didn\'t match any items. Try adjusting your criteria.',
    iconColor: poolwerxColors.grey[600]
  },
  'no-products': {
    icon: <ProductIcon />,
    title: 'No Products Found',
    description: 'No products are available for this outlet. Products may still be syncing from Lightspeed.',
    iconColor: poolwerxColors.teal
  },
  'no-sales': {
    icon: <SalesIcon />,
    title: 'No Sales Data',
    description: 'No sales records found for the selected period. Sales data is synced from Lightspeed regularly.',
    iconColor: poolwerxColors.red
  },
  'no-clients': {
    icon: <ClientsIcon />,
    title: 'No Clients Found',
    description: 'No client records match your criteria. Client data is synced from Lightspeed.',
    iconColor: poolwerxColors.teal
  },
  'no-reports': {
    icon: <ReportsIcon />,
    title: 'No Reports Available',
    description: 'No reports have been generated yet. Reports are created based on available data.',
    iconColor: poolwerxColors.grey[600]
  },
  'filtered': {
    icon: <FilterIcon />,
    title: 'No Items Match Filters',
    description: 'No items match your current filter settings. Try clearing some filters to see more results.',
    iconColor: poolwerxColors.warning
  },
  'custom': {
    icon: <NoDataIcon />,
    title: 'Empty',
    description: 'No content available.',
    iconColor: poolwerxColors.grey[600]
  }
}

// ============================================================================
// Main Component
// ============================================================================

const EmptyState: React.FC<EmptyStateProps> = ({
  type = 'no-data',
  title,
  description,
  icon,
  primaryAction,
  secondaryAction,
  compact = false,
  sx = {}
}) => {
  const config = EMPTY_STATE_CONFIGS[type]
  
  const displayTitle = title || config.title
  const displayDescription = description || config.description
  const displayIcon = icon || config.icon
  
  const iconSize = compact ? '3rem' : '4rem'
  const titleVariant = compact ? 'h6' : 'h5'
  const padding = compact ? 3 : 6
  
  const content = (
    <Box
      sx={{
        textAlign: 'center',
        py: padding,
        ...sx
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          color: config.iconColor || poolwerxColors.grey[600],
          mb: 2
        }}
      >
        {React.isValidElement(displayIcon) ? (
          React.cloneElement(displayIcon as React.ReactElement, {
            sx: { fontSize: iconSize, ...(displayIcon.props?.sx || {}) }
          })
        ) : (
          <SvgIcon sx={{ fontSize: iconSize }}>
            {displayIcon}
          </SvgIcon>
        )}
      </Box>
      
      {/* Title */}
      <Typography 
        variant={titleVariant} 
        component="h3"
        gutterBottom 
        sx={{ fontWeight: 'bold', color: 'text.primary' }}
      >
        {displayTitle}
      </Typography>
      
      {/* Description */}
      <Typography 
        variant="body1" 
        color="text.secondary" 
        sx={{ 
          mb: (primaryAction || secondaryAction) ? 3 : 0,
          maxWidth: '500px',
          mx: 'auto'
        }}
      >
        {displayDescription}
      </Typography>
      
      {/* Action Buttons */}
      {(primaryAction || secondaryAction) && (
        <Stack 
          direction={compact ? 'column' : 'row'} 
          spacing={2} 
          justifyContent="center"
          alignItems="center"
        >
          {primaryAction && (
            <Button
              variant={primaryAction.variant || 'contained'}
              onClick={primaryAction.onClick}
              size={compact ? 'medium' : 'large'}
              sx={{ minWidth: compact ? '100%' : '140px' }}
            >
              {primaryAction.label}
            </Button>
          )}
          
          {secondaryAction && (
            <Button
              variant={secondaryAction.variant || 'outlined'}
              onClick={secondaryAction.onClick}
              size={compact ? 'medium' : 'large'}
              sx={{ minWidth: compact ? '100%' : '140px' }}
            >
              {secondaryAction.label}
            </Button>
          )}
        </Stack>
      )}
    </Box>
  )
  
  if (compact) {
    return content
  }
  
  return (
    <Card sx={{ backgroundColor: '#f8f9fa', border: '2px dashed #dee2e6' }}>
      <CardContent>
        {content}
      </CardContent>
    </Card>
  )
}

export default EmptyState

// ============================================================================
// Specialized Empty State Components
// ============================================================================

/**
 * 📦 No products empty state
 */
export const NoProductsState: React.FC<{
  onRefresh?: () => void;
  onViewAll?: () => void;
}> = ({ onRefresh, onViewAll }) => (
  <EmptyState
    type="no-products"
    primaryAction={onRefresh ? {
      label: 'Refresh Data',
      onClick: onRefresh
    } : undefined}
    secondaryAction={onViewAll ? {
      label: 'View All Products',
      onClick: onViewAll
    } : undefined}
  />
)

/**
 * 🧾 No sales empty state
 */
export const NoSalesState: React.FC<{
  onRefresh?: () => void;
  onChangePeriod?: () => void;
}> = ({ onRefresh, onChangePeriod }) => (
  <EmptyState
    type="no-sales"
    primaryAction={onRefresh ? {
      label: 'Refresh Sales',
      onClick: onRefresh
    } : undefined}
    secondaryAction={onChangePeriod ? {
      label: 'Change Period',
      onClick: onChangePeriod
    } : undefined}
  />
)

/**
 * 👥 No clients empty state
 */
export const NoClientsState: React.FC<{
  onRefresh?: () => void;
  onClearFilters?: () => void;
}> = ({ onRefresh, onClearFilters }) => (
  <EmptyState
    type="no-clients"
    primaryAction={onRefresh ? {
      label: 'Refresh Data',
      onClick: onRefresh
    } : undefined}
    secondaryAction={onClearFilters ? {
      label: 'Clear Filters',
      onClick: onClearFilters
    } : undefined}
  />
)

/**
 * 🔍 No search results empty state
 */
export const NoResultsState: React.FC<{
  searchQuery?: string;
  onClearSearch?: () => void;
  onClearFilters?: () => void;
}> = ({ searchQuery, onClearSearch, onClearFilters }) => (
  <EmptyState
    type="no-results"
    description={
      searchQuery 
        ? `No results found for "${searchQuery}". Try a different search term or clear your filters.`
        : 'Your search or filters didn\'t match any items. Try adjusting your criteria.'
    }
    primaryAction={onClearSearch ? {
      label: 'Clear Search',
      onClick: onClearSearch
    } : undefined}
    secondaryAction={onClearFilters ? {
      label: 'Clear Filters',
      onClick: onClearFilters
    } : undefined}
  />
)

/**
 * 📊 No reports empty state
 */
export const NoReportsState: React.FC<{
  onRefresh?: () => void;
  onCreate?: () => void;
}> = ({ onRefresh, onCreate }) => (
  <EmptyState
    type="no-reports"
    primaryAction={onCreate ? {
      label: 'Generate Report',
      onClick: onCreate
    } : undefined}
    secondaryAction={onRefresh ? {
      label: 'Refresh',
      onClick: onRefresh
    } : undefined}
  />
)

/**
 * 🗂️ Filtered empty state (when filters return no results)
 */
export const FilteredEmptyState: React.FC<{
  onClearFilters: () => void;
  activeFiltersCount?: number;
}> = ({ onClearFilters, activeFiltersCount }) => (
  <EmptyState
    type="filtered"
    description={`No items match your ${activeFiltersCount || 'current'} filter${
      activeFiltersCount === 1 ? '' : 's'
    }. Try clearing some filters to see more results.`}
    primaryAction={{
      label: 'Clear All Filters',
      onClick: onClearFilters
    }}
  />
)

/**
 * 📱 Compact empty state for small spaces
 */
export const CompactEmptyState: React.FC<{
  message: string;
  icon?: React.ReactNode;
}> = ({ message, icon }) => (
  <EmptyState
    type="custom"
    title={message}
    description=""
    icon={icon}
    compact
    sx={{ py: 2 }}
  />
)